import { useCan } from '@/components/can/Can';
import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import ManageSlabsContainer from '@/features/points-management/point-setup-forms/ManageSlabsContainer';
import { useDynamicLocale } from '@/hoslog-intl';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { SlabsData } from '@/services/pointConfiguration/pointConfiguration.types';
import { useSearch } from '@tanstack/react-router';
import * as React from 'react';
import { useDeletePointSlab } from '../../../hooks/usePointSlabMutations';
import { getColumns } from './Column';

const SlabsTable = ({
  data,
  isLoading,
  pagination,
  pointCategoryId,
  categoryName,
}: {
  data: SlabsData[];
  isLoading: boolean;
  pagination: Pagination;
  pointCategoryId: string;
  categoryName?: string;
}) => {
  const { onOpen } = useDialogFormStore();
  const { openConfirmation } = useConfirmationStore();
  const canCreate = useCan({ allOf: [PERMS.point.management.slab.create] });
  const search = useSearch({ from: '/(app)/points-management/points-configuration/affiliate/$pointCategoryId/slabs' });
  const { mutateAsync: deleteSlab } = useDeletePointSlab();
  const { t } = useDynamicLocale();

  const columns = React.useMemo(
    () =>
      getColumns({
        onOpen,
        openConfirmation,
        deleteSlab,
        pointCategoryId,
        categoryName,
        activeTab: search.activeTab,
        t,
      }),
    [onOpen, openConfirmation, deleteSlab, pointCategoryId, categoryName, search.activeTab, t],
  );

  const onAddnew = () => {
    onOpen({
      modalTitle: 'pointManagement.page.form.manageSlabs.title',
      title: t('pointManagement.page.form.manageSlabs.title'),
      okText: t('common.page.button.submit'),
      size: 'lg',
      cancelText: t('common.page.button.cancel'),
      component: (
        <ManageSlabsContainer
          pointCategoryId={pointCategoryId}
          categoryName={categoryName}
          activeTab={search.activeTab}
        />
      ),
    });
  };

  const { table } = useDataTable({
    data,
    columns,
    pageCount: pagination.totalPages,
    initialState: {
      sorting: [{ id: 'fromAmount', desc: true }],
      columnPinning: { right: ['actions'] },
    },
    meta: {
      title: `${categoryName ?? t('common.page.tableColumn.categoryName')}: ${t('pointManagement.page.form.table.title')}`,
      action: canCreate ? onAddnew : null,
      actionKey: canCreate ? 'onAddNew' : '',
      loading: isLoading,
      totalPage: pagination.totalItems,
      hideSearch: true,
    },
    getRowId: (originalRow) => originalRow.pointSlabId,
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <div>
      <DataTable table={table} className="rounded-xl border-t-2 border-muted">
        <DataTableToolbar />
      </DataTable>
    </div>
  );
};

export default SlabsTable;
